import type { ContactSupportItem } from '@cvo/ui/components';

interface ContactChannel {
  id: ContactSupportItem['id'];
  icon: ContactSupportItem['icon'];
  labelKey: string;
  metaKey: string;
  toHref: (value: string) => string;
  /** Rejects a value that survives trimming but cannot form a destination, e.g. a phone with no digits. */
  isValid?: (value: string) => boolean;
  /** The Help Center row shows its label only; the raw URL is not useful copy. */
  hideValue?: boolean;
  /** Leaves the app, so it opens in a new tab. tel: and mailto: hand off to the device instead. */
  external?: boolean;
}

export interface ContactSupportLink {
  href: string;
  /** Leaves the app (help centre URL) → open in a new tab. mailto:/tel:/FAQ stay in place. */
  external: boolean;
}

// Channels a tenant may configure, in display order. Absent config drops the row.
const CONTACT_CHANNELS: ContactChannel[] = [
  {
    id: 'phone',
    icon: 'phone',
    labelKey: 'ui.components.contactSupport.phoneLabel',
    metaKey: 'custom.meta.contactPhone',
    toHref: value => `tel:${value.replace(/[^+\d]/g, '')}`,
    isValid: value => /\d/.test(value),
  },
  {
    id: 'email',
    icon: 'email',
    labelKey: 'ui.components.contactSupport.emailLabel',
    metaKey: 'custom.meta.support.email',
    toHref: value => `mailto:${value}`,
  },
  {
    id: 'helpCenter',
    icon: 'help-circle',
    labelKey: 'ui.components.contactSupport.helpCenterLabel',
    metaKey: 'custom.meta.support.link',
    toHref: value => value,
    hideValue: true,
    external: true,
  },
];

/** Most direct channel first. The FAQ page is the last resort. */
const SUPPORT_PRIORITY: Array<ContactSupportItem['id']> = ['helpCenter', 'email', 'phone'];

/**
 * The support channels the tenant has configured, ready for the ContactSupport card or
 * SupportChannels, and the single "Contact support" target for error actions: the most direct
 * of those channels, or the FAQ page.
 */
export function useContactChannels() {
  const { t, tf } = useI18n();
  const { faqsLink } = useFaqsLink();

  const contactChannels = computed<ContactSupportItem[]>(() => CONTACT_CHANNELS.flatMap((channel) => {
    // Tenant meta is authored per locale, so a key can exist while holding only whitespace.
    const value = tf(channel.metaKey)?.trim();
    if (!value || channel.isValid?.(value) === false) return [];

    return [{
      id: channel.id,
      icon: channel.icon,
      label: t(channel.labelKey),
      value: channel.hideValue ? undefined : value,
      href: channel.toHref(value),
      external: channel.external ?? false,
    }];
  }));

  const contactSupportLink = computed<ContactSupportLink>(() => {
    for (const id of SUPPORT_PRIORITY) {
      const channel = contactChannels.value.find(item => item.id === id);
      if (channel) return { href: channel.href, external: channel.external ?? false };
    }

    return { href: faqsLink.value, external: false };
  });

  return { contactChannels, contactSupportLink };
}
